/**
 * Les briques du formulaire de fiche enfant.
 *
 * ## Pourquoi elles existent
 *
 * Les six formulaires d'origine réimplémentaient chacun leur label, leur
 * message d'erreur et leur case à cocher. Résultat visible : une erreur
 * s'affichait en rouge ici, en gris là, et nulle part dans l'étape « Sorties »,
 * où un clic sur « Suivant » ne faisait simplement rien.
 *
 * Une erreur qui ne s'affiche pas est un formulaire cassé, même quand le code
 * qui la produit est juste. Ces briques rendent l'oubli difficile : passer
 * `erreur` à un champ suffit, et le champ s'occupe du reste — couleur, texte,
 * `aria-invalid`, liaison au message.
 *
 * ## Rouge quand on se trompe, orange quand il manque
 *
 * Ce ne sont pas la même chose. Dans le formulaire de création, un message
 * apparaît parce que le parent vient de se tromper : le rouge est juste. Dans
 * la vérification d'une fiche déjà enregistrée, les mêmes messages sont là dès
 * l'ouverture — la fiche date d'avant ces questions, personne ne s'est trompé,
 * et une page rouge à l'arrivée se lit comme une accusation.
 *
 * `TonDesManques` bascule tous les champs qu'il enveloppe en orange. Le ton est
 * porté par un contexte plutôt que par une propriété, parce qu'il traverse cinq
 * étapes et une quarantaine de champs : le passer de main en main ferait
 * qu'un seul oubli laisserait un champ rouge au milieu des autres.
 *
 * ## Le « pourquoi » se replie, il ne disparaît pas
 *
 * On demande à des familles un numéro de registre national, des antécédents
 * médicaux, un handicap. Chacune de ces questions mérite une réponse à « à
 * quoi ça sert ? », et aucune ne mérite d'occuper l'écran en permanence : d'où
 * le `<details>`, ouvert par qui se pose la question.
 */

import { createContext, useContext, useId } from 'react';
import { HelpCircle } from 'lucide-react';
import clsx from 'clsx';

/* -------------------------------------------------------------------------- */
/* Le ton des messages                                                         */
/* -------------------------------------------------------------------------- */

type Ton = 'erreur' | 'manque';

const ContexteTon = createContext<Ton>('erreur');

/** Enveloppe une étape pour que ses manques s'affichent en orange, non en rouge. */
export const TonDesManques = ({ children }: { children: React.ReactNode }) => (
  <ContexteTon.Provider value="manque">{children}</ContexteTon.Provider>
);

const useTon = () => useContext(ContexteTon);

/** Les couleurs d'un champ signalé, selon le ton en vigueur. */
const COULEURS = {
  erreur: {
    texte: 'text-red-600',
    bordure: 'border-red-400 bg-red-50/40',
    bordureLegere: 'border-red-300',
  },
  manque: {
    texte: 'text-amber-700',
    bordure: 'border-amber-400 bg-amber-50/60',
    bordureLegere: 'border-amber-300 bg-amber-50/40',
  },
} as const;

/**
 * L'astérisque des champs obligatoires.
 *
 * Le parent découvrait qu'un champ était requis en cliquant sur « Continuer » :
 * le nom du médecin, par exemple, n'avait rien qui le distinguait des champs
 * facultatifs. L'astérisque le dit avant, et `MentionObligatoire` dit ce
 * qu'il veut dire.
 */
export const Etoile = () => (
  <>
    <span className="ml-0.5 text-red-600" aria-hidden="true">
      *
    </span>
    <span className="sr-only"> (obligatoire)</span>
  </>
);

/** La légende de l'astérisque, une fois en haut du formulaire. */
export const MentionObligatoire = () => (
  <p className="text-xs text-gray-500">
    <span className="text-red-600" aria-hidden="true">
      *
    </span>{' '}
    champ obligatoire
  </p>
);

/* -------------------------------------------------------------------------- */
/* Structure                                                                   */
/* -------------------------------------------------------------------------- */

/** Un groupe de questions, avec son titre et ce qu'il sert à savoir. */
export const Bloc = ({
  titre,
  intro,
  requis,
  children,
}: {
  titre: string;
  intro?: React.ReactNode;
  /** Le bloc entier attend une réponse (une liste de choix, des oui/non…). */
  requis?: boolean;
  children: React.ReactNode;
}) => (
  <section className="space-y-5">
    <div>
      <h3 className="text-lg font-semibold text-gray-900">
        {titre}
        {requis && <Etoile />}
      </h3>
      {intro && <p className="mt-1 text-sm leading-relaxed text-gray-600">{intro}</p>}
    </div>
    {children}
  </section>
);

/** Le repli « pourquoi cette question ? ». */
export const Pourquoi = ({ children }: { children: React.ReactNode }) => (
  <details className="group mt-1.5">
    <summary className="inline-flex cursor-pointer list-none items-center text-xs font-medium text-gray-500 hover:text-primary-600">
      <HelpCircle className="mr-1 h-3.5 w-3.5" />
      Pourquoi cette question&nbsp;?
    </summary>
    <p className="mt-1.5 rounded-lg bg-gray-50 p-3 text-xs leading-relaxed text-gray-600">
      {children}
    </p>
  </details>
);

/** Un encadré d'information : ce que la famille doit savoir avant de répondre. */
export const Info = ({
  ton = 'neutre',
  titre,
  children,
}: {
  ton?: 'neutre' | 'attention';
  titre?: string;
  children: React.ReactNode;
}) => (
  <div
    className={clsx(
      'rounded-xl border p-4 text-sm leading-relaxed',
      ton === 'attention'
        ? 'border-amber-200 bg-amber-50 text-amber-900'
        : 'border-primary-100 bg-primary-50 text-gray-700',
    )}
  >
    {titre && <p className="mb-1.5 font-semibold text-gray-900">{titre}</p>}
    {children}
  </div>
);

/* -------------------------------------------------------------------------- */
/* Champs                                                                      */
/* -------------------------------------------------------------------------- */

interface BaseChamp {
  label: string;
  erreur?: string;
  aide?: React.ReactNode;
  pourquoi?: React.ReactNode;
  facultatif?: boolean;
  /** Affiche l'astérisque : la règle elle-même vit dans `fiche.ts`. */
  requis?: boolean;
}

/** L'habillage commun : label, aide, message d'erreur. */
const Habillage = ({
  id,
  label,
  erreur,
  aide,
  pourquoi,
  facultatif,
  requis,
  children,
}: BaseChamp & { id: string; children: React.ReactNode }) => {
  const ton = useTon();
  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-sm font-medium text-gray-700">
        {label}
        {requis && <Etoile />}
        {facultatif && <span className="ml-2 text-xs font-normal text-gray-400">facultatif</span>}
      </label>
      {children}
      {erreur ? (
        <p id={`${id}-erreur`} className={clsx('mt-1 text-sm', COULEURS[ton].texte)}>
          {erreur}
        </p>
      ) : (
        aide && <p className="mt-1 text-xs text-gray-500">{aide}</p>
      )}
      {pourquoi && <Pourquoi>{pourquoi}</Pourquoi>}
    </div>
  );
};

const useClassesSaisie = (erreur?: string) => {
  const ton = useTon();
  return clsx(
    'w-full rounded-lg border px-3 py-2 text-gray-900 transition-colors',
    'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent',
    erreur ? COULEURS[ton].bordure : 'border-gray-300',
  );
};

export const Saisie = ({
  valeur,
  onChange,
  type = 'text',
  placeholder,
  autoComplete,
  inputMode,
  ...base
}: BaseChamp & {
  valeur: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  autoComplete?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
}) => {
  const id = useId();
  const classes = useClassesSaisie(base.erreur);
  return (
    <Habillage id={id} {...base}>
      <input
        id={id}
        type={type}
        value={valeur}
        placeholder={placeholder}
        autoComplete={autoComplete}
        inputMode={inputMode}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={!!base.erreur}
        aria-describedby={base.erreur ? `${id}-erreur` : undefined}
        className={classes}
      />
    </Habillage>
  );
};

export const ZoneTexte = ({
  valeur,
  onChange,
  placeholder,
  lignes = 4,
  ...base
}: BaseChamp & {
  valeur: string;
  onChange: (v: string) => void;
  placeholder?: string;
  lignes?: number;
}) => {
  const id = useId();
  const classes = useClassesSaisie(base.erreur);
  return (
    <Habillage id={id} {...base}>
      <textarea
        id={id}
        rows={lignes}
        value={valeur}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={!!base.erreur}
        aria-describedby={base.erreur ? `${id}-erreur` : undefined}
        className={classes}
      />
    </Habillage>
  );
};

export const Liste = ({
  valeur,
  onChange,
  options,
  vide = 'Sélectionner…',
  ...base
}: BaseChamp & {
  valeur: string;
  onChange: (v: string) => void;
  options: { valeur: string; libelle: string }[];
  vide?: string;
}) => {
  const id = useId();
  const classes = useClassesSaisie(base.erreur);
  return (
    <Habillage id={id} {...base}>
      <select
        id={id}
        value={valeur}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={!!base.erreur}
        aria-describedby={base.erreur ? `${id}-erreur` : undefined}
        className={classes}
      >
        <option value="">{vide}</option>
        {options.map((o) => (
          <option key={o.valeur} value={o.valeur}>
            {o.libelle}
          </option>
        ))}
      </select>
    </Habillage>
  );
};

/** Une case à cocher, avec sa zone de clic entière. */
export const Case = ({
  coche,
  onChange,
  label,
  aide,
  erreur,
  requis,
}: {
  coche: boolean;
  onChange: (v: boolean) => void;
  label: React.ReactNode;
  aide?: React.ReactNode;
  erreur?: string;
  requis?: boolean;
}) => {
  const id = useId();
  const ton = useTon();
  return (
    <div>
      <label
        htmlFor={id}
        className={clsx(
          'flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors',
          erreur
            ? COULEURS[ton].bordureLegere
            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50',
        )}
      >
        <input
          id={id}
          type="checkbox"
          checked={coche}
          onChange={(e) => onChange(e.target.checked)}
          aria-invalid={!!erreur}
          className="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
        <span className="text-sm leading-relaxed text-gray-700">
          {label}
          {requis && <Etoile />}
          {aide && <span className="mt-1 block text-xs text-gray-500">{aide}</span>}
        </span>
      </label>
      {erreur && <p className={clsx('mt-1 text-sm', COULEURS[ton].texte)}>{erreur}</p>}
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* Choix                                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Un groupe de boutons radio présentés en cartes.
 *
 * Une liste déroulante aurait tenu moins de place, mais elle cache les options
 * — et sur les questions qui comptent ici (le niveau de natation, la forme des
 * photos publiées), ce qu'on ne voit pas ne se choisit pas.
 */
export function Choix<T extends string>({
  valeur,
  onChange,
  options,
  label,
  erreur,
  aide,
  pourquoi,
  colonnes = 1,
  requis,
}: {
  valeur: T | null;
  onChange: (v: T) => void;
  options: { valeur: T; libelle: string; detail?: string }[];
  label?: string;
  erreur?: string;
  aide?: React.ReactNode;
  pourquoi?: React.ReactNode;
  colonnes?: 1 | 2;
  requis?: boolean;
}) {
  const nom = useId();
  const ton = useTon();
  return (
    <fieldset>
      {label && (
        <legend className="mb-2 text-sm font-medium text-gray-700">
          {label}
          {requis && <Etoile />}
        </legend>
      )}
      <div className={clsx('grid gap-2', colonnes === 2 && 'sm:grid-cols-2')}>
        {options.map((o) => {
          const actif = valeur === o.valeur;
          return (
            <label
              key={o.valeur}
              className={clsx(
                'flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors',
                actif
                  ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-500'
                  : erreur
                    ? clsx(COULEURS[ton].bordureLegere, 'hover:bg-gray-50')
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50',
              )}
            >
              <input
                type="radio"
                name={nom}
                checked={actif}
                onChange={() => onChange(o.valeur)}
                className="mt-0.5 h-4 w-4 flex-shrink-0 border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm leading-relaxed">
                <span className={clsx('block', actif ? 'font-medium text-primary-900' : 'text-gray-700')}>
                  {o.libelle}
                </span>
                {o.detail && <span className="mt-0.5 block text-xs text-gray-500">{o.detail}</span>}
              </span>
            </label>
          );
        })}
      </div>
      {erreur ? (
        <p className={clsx('mt-1.5 text-sm', COULEURS[ton].texte)}>{erreur}</p>
      ) : (
        aide && <p className="mt-1.5 text-xs text-gray-500">{aide}</p>
      )}
      {pourquoi && <Pourquoi>{pourquoi}</Pourquoi>}
    </fieldset>
  );
}

/**
 * La paire « Oui / Non » du droit à l'image.
 *
 * Volontairement sans valeur par défaut : `valeur` vaut `null` tant que la
 * famille n'a pas répondu, et le formulaire ne la laisse pas passer. Un
 * consentement pré-coché n'est pas un consentement.
 */
export const OuiNon = ({
  valeur,
  onChange,
  erreur,
}: {
  valeur: 'oui' | 'non' | null;
  onChange: (v: 'oui' | 'non') => void;
  erreur?: string;
}) => {
  const nom = useId();
  const ton = useTon();
  return (
    <div>
      <div className="flex gap-2">
        {(['oui', 'non'] as const).map((v) => {
          const actif = valeur === v;
          return (
            <label
              key={v}
              className={clsx(
                'flex min-w-[5.5rem] cursor-pointer items-center justify-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium transition-colors',
                actif && v === 'oui' && 'border-green-500 bg-green-50 text-green-800 ring-1 ring-green-500',
                actif && v === 'non' && 'border-gray-500 bg-gray-100 text-gray-900 ring-1 ring-gray-500',
                !actif &&
                  (erreur
                    ? clsx(COULEURS[ton].bordureLegere, 'text-gray-600 hover:bg-gray-50')
                    : 'border-gray-300 text-gray-600 hover:bg-gray-50'),
              )}
            >
              <input
                type="radio"
                name={nom}
                checked={actif}
                onChange={() => onChange(v)}
                className="sr-only"
              />
              {v === 'oui' ? 'Oui' : 'Non'}
            </label>
          );
        })}
      </div>
      {erreur && <p className={clsx('mt-1.5 text-sm', COULEURS[ton].texte)}>{erreur}</p>}
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* Navigation d'étape                                                          */
/* -------------------------------------------------------------------------- */

/**
 * Une étape ouverte pour modifier une fiche déjà enregistrée, depuis sa page.
 *
 * Les écrans sont ceux de la création — mêmes champs, mêmes règles, mêmes
 * messages — mais le bas de l'écran change de sens : il n'y a ni étape
 * précédente ni suivante, on annule ou on enregistre. Porté par un contexte,
 * comme le ton des manques, pour ne pas ajouter trois propriétés aux cinq
 * étapes qui ne font que les transmettre.
 */
interface Modification {
  onAnnuler: () => void;
  enCours: boolean;
}

const ContexteModification = createContext<Modification | null>(null);

export const EnModification = ({
  onAnnuler,
  enCours,
  children,
}: Modification & { children: React.ReactNode }) => (
  <ContexteModification.Provider value={{ onAnnuler, enCours }}>{children}</ContexteModification.Provider>
);

export const Navigation = ({
  onPrecedent,
  onSuivant,
  libelleSuivant = 'Continuer',
  enCours = false,
}: {
  onPrecedent?: () => void;
  onSuivant?: () => void;
  libelleSuivant?: string;
  enCours?: boolean;
}) => {
  const modification = useContext(ContexteModification);
  if (modification) {
    return (
      <div className="flex flex-col-reverse gap-3 border-t border-gray-200 pt-6 sm:flex-row sm:justify-end">
        <button type="button" onClick={modification.onAnnuler} disabled={modification.enCours} className="btn-outline">
          Annuler
        </button>
        <button
          type="button"
          onClick={onSuivant}
          disabled={modification.enCours}
          className="btn-primary inline-flex items-center justify-center disabled:opacity-60"
        >
          {modification.enCours ? 'Enregistrement…' : 'Enregistrer'}
        </button>
      </div>
    );
  }
  return (
    <div className="flex flex-col-reverse gap-3 border-t border-gray-200 pt-6 sm:flex-row sm:justify-between">
      {onPrecedent ? (
        <button type="button" onClick={onPrecedent} className="btn-outline">
          Précédent
        </button>
      ) : (
        <span />
      )}
      <button type={onSuivant ? 'button' : 'submit'} onClick={onSuivant} disabled={enCours} className="btn-primary disabled:opacity-60">
        {enCours ? 'Enregistrement…' : libelleSuivant}
      </button>
    </div>
  );
};
